import React, { useCallback, useContext, useMemo } from "react";
import { Box, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { WfLinkGhost } from "src/components/base/wf-link";
import { AggregatedReactionsBar } from "src/components/common/aggregated-reactions-bar/aggregated-reactions-bar";
import { IFeedCardStockLoverNewsBody } from "src/components/feed-card/types/content";
import { EnumFeedClickTrackingArea, useFeedClickTracking } from "src/components/pages/feed/hooks/use-feed-click-tracking";
import { useDateTimeFormatter } from "src/hooks/formatters/use-date-time-formatter";
import { FeedCardContext } from "../../feed-card";
import { TotalInteractions } from "./total-interactions";

export const FeedCardStockLoverNews = ({ text, date, url, aggregatedReactions, likeInfo, commentInfo }: IFeedCardStockLoverNewsBody) => {
    const context = useContext(FeedCardContext);
    const { formatDateShort, formatTimeShort } = useDateTimeFormatter();
    const { trackFeedClick } = useFeedClickTracking();
    const { t } = useTranslation("feed");
    const commentCount = context.commentCount ?? commentInfo?.count;
    const likeCount = context.likeCount ?? likeInfo?.count;

    const formattedComments = useMemo(() => (commentCount ? t("stock-lover-news.comments", { count: commentCount }) : null), [commentCount, t]);

    const getFormattedHeadline = useCallback(
        (time: string) => {
            const articleTime = new Date(time);

            return (
                <Text fontSize="xs">
                    {formatDateShort(articleTime)} &bull; {formatTimeShort(articleTime)}
                    {formattedComments && <> &bull; {formattedComments}</>}
                </Text>
            );
        },
        [formatDateShort, formatTimeShort, formattedComments]
    );

    const onNewsLinkClick = () => {
        trackFeedClick(context.contentId, url, EnumFeedClickTrackingArea.Body);
    };

    const shouldShrinkReactionsBarOnMob = likeInfo || commentInfo;

    return (
        <VStack align="flex-start" spacing={shouldShrinkReactionsBarOnMob ? [0, 1] : 1}>
            {aggregatedReactions && (
                <HStack width="100%" justifyContent="space-between" py={1}>
                    <Box width="100%" maxWidth={shouldShrinkReactionsBarOnMob ? ["180px", "300px"] : ["100%", "300px"]}>
                        <AggregatedReactionsBar
                            topNumberOfReactions={aggregatedReactions.topNumberOfReactions}
                            reactions={aggregatedReactions.reactions}
                        />
                    </Box>
                    <HStack spacing={[1]}>
                        <TotalInteractions commentCount={commentCount} likeCount={likeCount} reactions={aggregatedReactions.reactions} />
                    </HStack>
                </HStack>
            )}
            <Box width="100%">
                <Flex alignItems="baseline" justifyContent="space-between">
                    <Text>{getFormattedHeadline(date)}</Text>
                    {!aggregatedReactions ? (
                        <HStack spacing={[1]} py={1}>
                            <TotalInteractions commentCount={commentCount} likeCount={likeCount} />
                        </HStack>
                    ) : null}
                </Flex>
                <WfLinkGhost target="_blank" href={url} fontSize="md" fontWeight="semibold" onClick={onNewsLinkClick}>
                    {text}
                </WfLinkGhost>
            </Box>
        </VStack>
    );
};
